<template>
    <CqModal :showModal="showModal" @closeModal="closeModal" label="Thema- en kleurinstellingen">
        <h2>Thema</h2>
        <CqSelectInput label="Thema" :options="THEMES" v-model="theme" />
        <template v-if="areWorkSchedulesEnabled">
            <h2>Kleuren</h2>
            <h3>Vervulling werkindelingen</h3>
            <ThemeColorSetting v-model="workScheduleStatusNone" label="Verplichte werkindelingen niet ingevuld" />
            <ThemeColorSetting v-model="workScheduleStatusPartial" label="Verplichte werkindelingen gedeeltelijk ingevuld" />
            <ThemeColorSetting v-model="workScheduleStatusFull" label="Verplichte werkindelingen volledig ingevuld" />
            <h3>Paraatheid</h3>
            <ThemeColorSetting v-model="workloadRequirementsBelowMinimum" label="Onder minimum" />
            <ThemeColorSetting v-model="workloadRequirementsBelowOptimum" label="Onder optimum" />
            <ThemeColorSetting v-model="workloadRequirementsOptimum" label="Rond optimum" />
            <ThemeColorSetting v-model="workloadRequirementsAboveOptimum" label="Boven optimum" />
            <ThemeColorSetting v-model="workloadRequirementsAboveMaximum" label="Boven maximum" />
        </template>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {CqModal, CqSelectInput, StringSelectInputOption} from '@cquential/ui';
    import {CSSHexColorString} from '@/types';
    import TypeUtil from '@/util/TypeUtil';
    import ThemeColorSetting from '@/components/modals/ThemeSettings/ThemeColorSetting.vue';

    @Component({
        components: {
            CqModal,
            CqSelectInput,
            ThemeColorSetting,
        },
        emits: ['update:preference', 'closeModal'],
    })
    export default class ThemeSettings extends Vue {
        @Prop({required: true}) public preferences!: Record<string, string | number>;
        @Prop({type: Boolean}) public areWorkSchedulesEnabled!: boolean;
        @Prop({type: Boolean}) public showModal!: boolean;

        private static readonly THEMES: StringSelectInputOption[] = [
            {
                label: 'Apparaatstandaard',
                value: '',
            },
            {
                label: 'Licht',
                value: 'light',
            },
            {
                label: 'Donker',
                value: 'dark',
            },
        ];

        public get THEMES(): StringSelectInputOption[] {
            return ThemeSettings.THEMES;
        }

        public get theme(): string | undefined {
            return 'planbordTheme' in this.preferences && typeof this.preferences.planbordTheme === 'string' ? this.preferences.planbordTheme : '';
        }

        public set theme(themeName: string | undefined) {
            this.$emit('update:preference', 'planbordTheme', themeName ? themeName : undefined);
        }

        public get workloadRequirementsBelowMinimum(): CSSHexColorString | undefined {
            return this.readColorFromPreferences('themeColor:requirementBelowMinimum', '#dc143c');
        }

        public set workloadRequirementsBelowMinimum(color: CSSHexColorString | undefined) {
            this.$emit('update:preference', 'themeColor:requirementBelowMinimum', color);
        }

        public get workloadRequirementsBelowOptimum(): CSSHexColorString | undefined {
            return this.readColorFromPreferences('themeColor:requirementBelowOptimum', '#ffd700');
        }

        public set workloadRequirementsBelowOptimum(color: CSSHexColorString | undefined) {
            this.$emit('update:preference', 'themeColor:requirementBelowOptimum', color);
        }

        public get workloadRequirementsOptimum(): CSSHexColorString | undefined {
            return this.readColorFromPreferences('themeColor:requirementOptimum', '#8fbc8f');
        }

        public set workloadRequirementsOptimum(color: CSSHexColorString | undefined) {
            this.$emit('update:preference', 'themeColor:requirementOptimum', color);
        }

        public get workloadRequirementsAboveOptimum(): CSSHexColorString | undefined {
            return this.readColorFromPreferences('themeColor:requirementAboveOptimum', '#87cefa');
        }

        public set workloadRequirementsAboveOptimum(color: CSSHexColorString | undefined) {
            this.$emit('update:preference', 'themeColor:requirementAboveOptimum', color);
        }

        public get workloadRequirementsAboveMaximum(): CSSHexColorString | undefined {
            return this.readColorFromPreferences('themeColor:requirementAboveMaximum', '#9400d3');
        }

        public set workloadRequirementsAboveMaximum(color: CSSHexColorString | undefined) {
            this.$emit('update:preference', 'themeColor:requirementAboveMaximum', color);
        }

        public get workScheduleStatusFull(): CSSHexColorString | undefined {
            return this.readColorFromPreferences('themeColor:workScheduleStatusFull', '#8fbc8f');
        }

        public set workScheduleStatusFull(color: CSSHexColorString | undefined) {
            this.$emit('update:preference', 'themeColor:workScheduleStatusFull', color);
        }

        public get workScheduleStatusPartial(): CSSHexColorString | undefined {
            return this.readColorFromPreferences('themeColor:workScheduleStatusPartial', '#ffa500');
        }

        public set workScheduleStatusPartial(color: CSSHexColorString | undefined) {
            this.$emit('update:preference', 'themeColor:workScheduleStatusPartial', color);
        }

        public get workScheduleStatusNone(): CSSHexColorString | undefined {
            return this.readColorFromPreferences('themeColor:workScheduleStatusNone', '#dc143c');
        }

        public set workScheduleStatusNone(color: CSSHexColorString | undefined) {
            this.$emit('update:preference', 'themeColor:workScheduleStatusNone', color);
        }

        private readColorFromPreferences(name: string, fallback: CSSHexColorString | undefined): CSSHexColorString | undefined {
            if (name in this.preferences) {
                const color: string | number = this.preferences[name]; // need simple variable for typescript to understand type check
                if (TypeUtil.isValidCSSHexColorString(color)) {
                    return color;
                }
            }

            return fallback;
        }

        public closeModal(): void {
            this.$emit('closeModal');
        }
    }
</script>

<style lang="scss" scoped>
    h2, h3 {
        margin: 0 0 0.25rem;
    }

    h2:not(:first-child) {
        margin-top: 1.5rem;
    }
</style>
